import { useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';

import { Order } from '~hooks/useOrders/models';
import {
  AllOrdersLoadedIndicator,
  VirtualizedOrdersLoadingIndicator,
} from '~pages/Approvals/Files/components/OrdersList/OrdersList';

import ChargesOrderSection from './ChargesOrderSection';

interface ChargesOrdersListProps {
  hasMore: boolean;
  hasUnfilteredResults: boolean;
  isLoading: boolean;
  onClearFilters: () => void;
  onLoadMore: () => void;
  orders: Order[];
}

export default function ChargesOrdersList({
  hasMore,
  hasUnfilteredResults,
  isLoading,
  onClearFilters,
  onLoadMore,
  orders,
}: ChargesOrdersListProps) {
  const itemCount = orders.length + 1;

  const handleEndReached = useCallback(() => {
    if (hasMore && !isLoading) {
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore]);

  const renderItem = useCallback(
    (index: number) => {
      if (index < orders.length) {
        return <ChargesOrderSection key={orders[index].id} order={orders[index]} />;
      }

      if (isLoading) {
        return <VirtualizedOrdersLoadingIndicator />;
      }

      return (
        <AllOrdersLoadedIndicator
          hasOrders={orders.length > 0}
          onClearFilters={onClearFilters}
          showClearFiltersHint={orders.length === 0 && hasUnfilteredResults}
        />
      );
    },
    [hasUnfilteredResults, isLoading, onClearFilters, orders],
  );

  return (
    <Virtuoso
      data={Array.from({ length: itemCount }, (_, index) => index)}
      endReached={handleEndReached}
      itemContent={renderItem}
      overscan={5}
      useWindowScroll
    />
  );
}
